import { authedJSON } from './http'
import type { InstrumentsResponse } from './types'

export type BarRange = '1D' | '1W' | '1M' | '3M' | '1Y'
export type BarInterval = '1s' | '1m' | '5m' | '15m' | '1h' | '1d' | '1w'

export interface BarPoint {
  t: number
  o: number
  h: number
  l: number
  c: number
  v: number
}

export interface BarSeries {
  symbol: string
  range: BarRange | 'custom'
  interval: BarInterval
  bars: BarPoint[]
}

export function getTickerBars(
  symbol: string,
  range: BarRange,
  interval?: BarInterval,
): Promise<BarSeries> {
  const qs = new URLSearchParams({ symbol, range })
  if (interval) qs.set('interval', interval)
  return authedJSON<BarSeries>(`/api/marketdata/bars?${qs.toString()}`)
}

export function clearBarCache(
  symbol: string,
  interval: BarInterval,
): Promise<{ symbol: string; interval: BarInterval; deleted: number }> {
  const qs = new URLSearchParams({ symbol, interval })
  return authedJSON(`/api/marketdata/cache?${qs.toString()}`, { method: 'DELETE' })
}

// Live symbol search (Schwab /instruments: symbol prefix + company name).
// The SQLite cache in src/db/instruments.ts layers on top of this.
export function searchInstrumentsRemote(
  q: string,
  limit = 20,
): Promise<InstrumentsResponse> {
  const qs = new URLSearchParams({ q, limit: String(limit) })
  return authedJSON<InstrumentsResponse>(`/api/marketdata/instruments/search?${qs.toString()}`)
}

// ---- Instrument fundamentals (Schwab /instruments?projection=fundamental) ----
// Slow-moving data; consumers should cache with a long staleTime.

export interface InstrumentFundamental {
  marketCap: number | null
  sharesOutstanding: number | null
  peRatio: number | null
  pegRatio: number | null
  pbRatio: number | null
  beta: number | null
  eps: number | null
  epsTTM: number | null
  high52: number | null
  low52: number | null
  divAmount: number | null
  divYield: number | null
  divFreq: number | null
  divDate: string | null
  nextDivDate: string | null
  nextDivPayDate: string | null
  divPayAmount: number | null
  grossMarginTTM: number | null
  netProfitMarginTTM: number | null
  operatingMarginTTM: number | null
  returnOnEquity: number | null
  returnOnAssets: number | null
  returnOnInvestment: number | null
  shortIntToFloat: number | null
  shortIntDayToCover: number | null
  avg10DaysVolume: number | null
  avg1YearVolume: number | null
}

export interface InstrumentFundamentals {
  symbol: string
  name: string | null
  exchange: string | null
  assetType: string | null
  fundamental: InstrumentFundamental | null
}

export function getInstrumentFundamentals(symbol: string): Promise<InstrumentFundamentals> {
  const qs = new URLSearchParams({ symbol })
  return authedJSON<InstrumentFundamentals>(
    `/api/marketdata/instruments/fundamentals?${qs.toString()}`,
  )
}

// ---- Market hours calendar (Schwab /markets) ----

export interface MarketHoursSession {
  start: string // ISO 8601 with tz offset
  end: string
}

export interface MarketHoursDay {
  date: string
  equity: {
    isOpen: boolean
    pre: MarketHoursSession | null
    regular: MarketHoursSession | null
    post: MarketHoursSession | null
  }
}

export function getMarketHours(date?: string): Promise<MarketHoursDay> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : ''
  return authedJSON<MarketHoursDay>(`/api/marketdata/hours${qs}`)
}

export function getTickerBarsWindow(
  symbol: string,
  startMs: number,
  endMs: number,
  interval: BarInterval = '1m',
): Promise<BarSeries> {
  const qs = new URLSearchParams({
    symbol,
    startMs: String(Math.floor(startMs)),
    endMs: String(Math.floor(endMs)),
    interval,
  })
  return authedJSON<BarSeries>(`/api/marketdata/bars?${qs.toString()}`)
}
